import {
  Box,
  Collapse,
  Divider,
  Flex,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  Tooltip,
  useBoolean,
  VStack,
} from '@elkaso-app/web-design';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import IntlMessages from 'utils/intl-messages';
import { ArrowIcon } from './icons/arrow-icon';
import { DotIcon } from './icons/dot-icon';
// ItemIcon is defined inline below
import { TSidebarItem } from './utils/sidebar-items';

interface IItemIcon {
  icon: any;
  marginEnd?: string;
  fill?: string;
}

const ItemIcon = ({ icon: Icon, ...rest }: IItemIcon) => {
  return <Icon {...rest} />;
};

interface ISidebarItem {
  item: TSidebarItem;
  isCollapsed?: boolean;
}

export const SidebarItem = ({ item, isCollapsed }: ISidebarItem) => {
  const [isActive, setIsActive] = useState(false);
  const [isOpen, setIsOpen] = useBoolean(false);
  const location = useLocation();

  useEffect(() => {
    // Active when the item's own path matches, or (for groups whose children
    // don't share a path prefix, e.g. GPO) when any child path matches.
    const childMatch = item.children?.some((child) => location.pathname.startsWith(child.path)) ?? false;
    if (location.pathname.startsWith(item.path) || childMatch) {
      return setIsActive(true);
    }

    setIsActive(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isActive) return setIsOpen.on();

    () => {
      setIsOpen.off();
    };
  }, [isActive]);

  const handleIsOpen = () => {
    setIsOpen.toggle();
  };

  const isChildActive = (path: string) => {
    if (location.pathname.startsWith(path)) return true;
    return false;
  };

  // Group menu items by their group property
  const groupItems = (children: TSidebarItem['children']) => {
    if (!children) return { grouped: {}, ungrouped: [] };

    const grouped: Record<string, NonNullable<TSidebarItem['children']>> = {};
    const ungrouped: NonNullable<TSidebarItem['children']> = [];

    children.forEach((child) => {
      if (child.group) {
        if (!grouped[child.group]) {
          grouped[child.group] = [];
        }
        grouped[child.group].push(child);
      } else {
        ungrouped.push(child);
      }
    });

    return { grouped, ungrouped };
  };

  // Define group metadata with emoji icons and readable names
  const getGroupInfo = (groupKey: string) => {
    const groupMap: Record<string, { name: string; icon: string }> = {
      'brand-management': { name: 'Brand Management', icon: '🏢' },
      'contact-management': { name: 'Contact Management', icon: '📞' },
      licensing: { name: 'Licensing & Legal', icon: '📄' },
      'cuisine-management': { name: 'Cuisine Management', icon: '🍽️' },
      'location-services': { name: 'Location Services', icon: '🗺️' },
      'access-control': { name: 'Access Control', icon: '🔐' },
    };

    return groupMap[groupKey] || { name: groupKey, icon: '📋' };
  };

  // Render grouped children with dividers and headers
  const renderGroupedChildren = (children: TSidebarItem['children']) => {
    if (!children) return null;

    const { grouped, ungrouped } = groupItems(children);
    const elements: JSX.Element[] = [];

    // Render ungrouped items first
    if (ungrouped && ungrouped.length > 0) {
      elements.push(
        <VStack key='ungrouped' spacing='xs' align='start' p='sm' ps='24px'>
          {ungrouped.map((child) => {
            if (child.disable) return null;
            return renderMenuItem(child);
          })}
        </VStack>
      );
    }

    // Render grouped items
    if (grouped) {
      Object.entries(grouped).forEach(([groupKey, groupChildren], index) => {
        const groupInfo = getGroupInfo(groupKey);

        // Add divider before group (except for first group)
        if (elements.length > 0 || index > 0) {
          elements.push(
            <Box key={`divider-${groupKey}`} px='sm'>
              <Divider borderColor='gray.300' />
            </Box>
          );
        }

        // Add group header
        elements.push(
          <Box key={`header-${groupKey}`} px='sm' pt='xs' pb='xs'>
            <HStack spacing={2}>
              <Text fontSize='xs' fontWeight='semibold' color='gray.500' textTransform='uppercase' letterSpacing='wide'>
                {groupInfo.icon} {groupInfo.name}
              </Text>
            </HStack>
          </Box>
        );

        // Add group items
        elements.push(
          <VStack key={`items-${groupKey}`} spacing='xs' align='start' px='sm' pb='sm' ps='24px'>
            {groupChildren.map((child) => {
              if (child.disable) return null;
              return renderMenuItem(child);
            })}
          </VStack>
        );
      });
    }

    return elements;
  };

  // Render individual menu item
  const renderMenuItem = (child: NonNullable<TSidebarItem['children']>[0]) => (
    <NavLink key={child.path} to={child.path}>
      <HStack
        spacing='md'
        py={1}
        px={2}
        borderRadius='sm'
        _hover={{ bg: 'gray.100' }}
        transition='background-color 0.2s'>
        <DotIcon fill={isChildActive(child.path) ? 'red.500' : 'gray.400'} />
        <Text
          color={isChildActive(child.path) ? 'red.600' : 'gray.600'}
          fontSize='sm'
          fontWeight={isChildActive(child.path) ? 'medium' : 'normal'}>
          {child.name}
        </Text>
      </HStack>
    </NavLink>
  );

  // Check if item is disabled
  if (item.disable) return null;

  // Collapsed rail: icon-only entry. Leaf items get a tooltip, items with
  // children get a hover flyout listing the full submenu.
  if (isCollapsed) {
    const railIcon = (
      <Flex
        align='center'
        justify='center'
        boxSize='40px'
        mx='auto'
        borderRadius='base'
        cursor='pointer'
        bgColor={isActive ? 'red.500' : 'transparent'}
        _hover={{ bgColor: isActive ? 'red.500' : 'gray.100' }}
        _active={{ bgColor: isActive ? 'red.500' : 'gray.200' }}
        transition='background-color 0.2s'>
        <ItemIcon icon={item.icon} fill={isActive ? 'white' : 'gray.500'} />
      </Flex>
    );

    if (!item.children) {
      return (
        <NavLink to={item.path}>
          <Tooltip label={<IntlMessages id={item.name} />} placement='end' hasArrow openDelay={100}>
            {railIcon}
          </Tooltip>
        </NavLink>
      );
    }

    return (
      <Popover trigger='hover' placement='end-start' openDelay={100} closeDelay={200} isLazy>
        <PopoverTrigger>{railIcon}</PopoverTrigger>
        <Portal>
          <PopoverContent zIndex='popover' w='280px' maxH='75vh' overflowY='auto' boxShadow='lg'>
            <PopoverBody p={0}>
              <Box px='md' py='sm' borderBottom='1px solid' borderColor='gray.100'>
                <Text fontSize='sm' fontWeight='semibold' color='gray.700'>
                  <IntlMessages id={item.name} />
                </Text>
              </Box>

              <VStack spacing={0} align='stretch' py='xs'>
                {renderGroupedChildren(item.children)}
              </VStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  }

  // Sidebar item with children
  if (item.children) {
    return (
      <Box>
        <Flex
          p={'sm'}
          align={'center'}
          borderRadius='base'
          borderBottomEndRadius={isOpen ? 'unset' : 'base'}
          borderBottomStartRadius={isOpen ? 'unset' : 'base'}
          bgColor={isOpen && !isActive ? 'gray.50' : isActive ? 'red.500' : 'white'}
          cursor='pointer'
          userSelect={'none'}
          _hover={{ bgColor: isActive ? 'red.500' : 'gray.50' }}
          _active={{ bgColor: isActive ? 'red.500' : 'gray.200' }}
          onClick={handleIsOpen}>
          <ItemIcon icon={item.icon} marginEnd={'md'} fill={isActive ? 'white' : 'gray.500'} />

          <Text color={isActive ? 'white' : 'gray.500'} fontSize='sm' fontWeight='medium' whiteSpace='nowrap'>
            <IntlMessages id={item.name} />
          </Text>

          <ArrowIcon
            ms='auto'
            transform={isOpen ? 'rotate(0deg)' : 'rotate(180deg)'}
            transition='transform 0.2s ease'
            fill={isActive ? 'white' : 'gray.500'}
          />
        </Flex>

        <Collapse in={isOpen} unmountOnExit>
          <VStack
            spacing={0}
            align='stretch'
            borderBottomEndRadius='base'
            borderBottomStartRadius='base'
            bgColor='gray.50'>
            {renderGroupedChildren(item.children)}
          </VStack>
        </Collapse>
      </Box>
    );
  }

  // Sidebar item with no children
  return (
    <NavLink to={item.path}>
      <Flex
        p={'sm'}
        align={'center'}
        borderRadius='base'
        bgColor={isActive ? 'red.500' : 'white'}
        _hover={{ bgColor: isActive ? 'red.500' : 'gray.100' }}
        _active={{ bgColor: isActive ? 'red.500' : 'gray.200' }}>
        <ItemIcon icon={item.icon} marginEnd={'md'} fill={isActive ? 'white' : 'gray.500'} />
        <Text color={isActive ? 'white' : 'gray.500'} fontSize='sm' fontWeight='medium' whiteSpace='nowrap'>
          <IntlMessages id={item.name} />
        </Text>
      </Flex>
    </NavLink>
  );
};
